<script setup lang="ts">
// Host global do painel Minha Conta V2 — montado no layout default.
// Renderiza a tela ativa do estado do modal; as rotas /user/* são
// interceptadas pelo middleware userV2Modal e viram trocas de tela aqui.
//
// O Shell (header + saldos + sidebar + navegação) é montado UMA VEZ aqui,
// fora de cada tela — só o conteúdo interno troca via <component :is>. Isso
// evita que a transição de abrir/fechar rode de novo a cada navegação
// dentro do modal (ela só deve tocar quando o modal abre/fecha de verdade,
// controlado pelo v-if="isOpen" no Shell — trocar de tela não afeta esse
// v-if, só o <component> interno).
const { state } = useUserV2Modal();

interface ScreenEntry {
  component: ReturnType<typeof resolveComponent>;
  // repassados pro UserV2Shell (linha "‹ Título" no mobile); tela raiz/tab
  // (Perfil, Dados, Notificações, Conquistas) não usa, fica default do Shell
  screenTitle?: string;
  backTo?: string;
}

const screens: Record<string, ScreenEntry> = {
  profile: { component: resolveComponent('UserV2ScreenProfile') },
  data: { component: resolveComponent('UserV2ScreenDataMenu') },
  account: {
    component: resolveComponent('UserV2ScreenAccountData'),
    screenTitle: 'Dados da conta',
  },
  security: {
    component: resolveComponent('UserV2ScreenSecurity'),
    screenTitle: 'Login e segurança',
  },
  'login-history': {
    component: resolveComponent('UserV2ScreenLoginHistory'),
    screenTitle: 'Histórico de login',
  },
  'bets-history': {
    component: resolveComponent('UserV2ScreenBetsHistory'),
    screenTitle: 'Histórico de apostas',
  },
  'transactions-history': {
    component: resolveComponent('UserV2ScreenTransactionsHistory'),
    screenTitle: 'Histórico de transações',
  },
  'poker-history': {
    component: resolveComponent('UserV2ScreenPokerHistory'),
    screenTitle: 'Histórico de Rodadas de Poker',
  },
  wallet: {
    component: resolveComponent('UserV2ScreenWallet'),
    screenTitle: 'Carteira',
  },
  'last-withdraw': {
    component: resolveComponent('UserV2ScreenLastWithdraw'),
    screenTitle: 'Status de Saque',
  },
  notifications: { component: resolveComponent('UserV2ScreenNotifications') },
  achievements: { component: resolveComponent('UserV2ScreenAchievements') },
  refers: {
    component: resolveComponent('UserV2ScreenRefers'),
    screenTitle: 'Indique e Ganhe',
  },
  rewards: {
    component: resolveComponent('UserV2ScreenRewards'),
    screenTitle: 'Prêmios e recompensas',
    backTo: '/user/profile',
  },
  'income-report': {
    component: resolveComponent('UserV2ScreenIncomeReport'),
    screenTitle: 'Informe Rendimentos',
  },
  'self-exclusion': {
    component: resolveComponent('UserV2ScreenSelfExclusion'),
    screenTitle: 'Auto-Exclusão',
  },
  'temporary-pause': {
    component: resolveComponent('UserV2ScreenTemporaryPause'),
    screenTitle: 'Pausa Temporária',
  },
  'account-protection': {
    component: resolveComponent('UserV2ScreenAccountProtection'),
    screenTitle: 'Jogo Responsável',
  },
  'bank-accounts': {
    component: resolveComponent('UserV2ScreenBankAccounts'),
    screenTitle: 'Contas Bancárias',
  },
};

const activeScreen = computed<ScreenEntry | undefined>(
  () => screens[state.value.screen ?? ''],
);

// trava o scroll da página atrás enquanto o modal está aberto.
// Usa o contador compartilhado (useBodyScrollLock) em vez de mexer no
// body.style direto: assim não destrava o body quando outro modal
// (depósito, saque, Giratudo) ainda está aberto ao mesmo tempo.
const isOpen = computed(() => !!state.value.screen && !!activeScreen.value);

useBodyScrollLock(isOpen);
</script>

<template>
  <!-- Teleport pro body (escapa do stacking context do layout) por FORA da
       Transition — se fosse por dentro, o Vue não anima um componente cuja
       raiz é um Teleport quando os dois estão em componentes diferentes
       (vuejs/core#5836). -->
  <Teleport to="body">
    <Transition name="user-v2-slide">
      <UserV2Shell
        v-if="isOpen && activeScreen"
        :back-to="activeScreen.backTo"
        :screen-title="activeScreen.screenTitle"
      >
        <component
          :is="activeScreen.component"
          :key="state.screen + JSON.stringify(state.props)"
          v-bind="state.props"
        />
      </UserV2Shell>
    </Transition>
  </Teleport>
</template>
